/**
 * Customer FSC tariff "missing" state.
 *
 * The v2 quote migration no longer auto-creates a customer's fuel-surcharge
 * tariff: a CUSTOMER lane whose tariff isn't in v1 yet migrates with
 * fsctype=CUSTOMER but fsctariff=0. That pair is what we surface as
 * "Missing Tariff" (in the quote's Direct tab and the migration drill-down)
 * instead of a silent "—", so a CSR knows to assign one.
 */
export const MISSING_TARIFF_LABEL = 'Missing Tariff';

export function isMissingCustomerTariff(
  fscType: string | number | null | undefined,
  fscTariff: string | number | null | undefined,
): boolean {
  const type = String(fscType ?? '').trim().toUpperCase();
  const tariffNum = Number(fscTariff) || 0;
  return type === 'CUSTOMER' && tariffNum <= 0;
}

/**
 * Customer FSC tariff "deleted" state — the other way a lane's fuel link breaks.
 * Here the tariff WAS assigned, but every fueldata.fsc_tariff revision behind it
 * is soft-deleted (e.g. quote 36462-7 → tariff 1452), so nothing resolves and the
 * invoice charges no FSC. The backend resolves that against the fuel schema and
 * reports it as `fscTariffDeleted` on the quote detail; this pairs the flag with
 * the lane's own fuel type/number so the UI never strikes through a live tariff
 * (e.g. right after the CSR picks a replacement).
 */
export const DELETED_TARIFF_LABEL = 'Deleted';

export function isDeletedCustomerTariff(
  fscType: string | number | null | undefined,
  fscTariff: string | number | null | undefined,
  tariffDeleted: boolean | null | undefined,
): boolean {
  const type = String(fscType ?? '').trim().toUpperCase();
  const tariffNum = Number(fscTariff) || 0;
  return !!tariffDeleted && type === 'CUSTOMER' && tariffNum > 0;
}

/** Either broken state — never assigned, or assigned then deleted. Both leave the
 *  lane without a resolvable FSC, and both unlock the secured-quote exemption
 *  that lets a CSR point the lane at a live tariff. */
export function isBrokenCustomerTariff(
  fscType: string | number | null | undefined,
  fscTariff: string | number | null | undefined,
  tariffDeleted: boolean | null | undefined,
): boolean {
  return isMissingCustomerTariff(fscType, fscTariff)
    || isDeletedCustomerTariff(fscType, fscTariff, tariffDeleted);
}
